/**
 * CommittedLineCard — compact single-card render for a saved/committed line item.
 *
 * PR-19 (2026-07-22) established the component; PR-22 (2026-07-23) tightens
 * chrome + typography to hit wire W4 tokens exactly (see below).
 *
 * Wire authority:
 *   • QB-W-add-item-wizard-STATES.html · W4 (saved → optimised line card)
 *   • QB-LINE-line-item-accessories-FLOW.html · S4 (same shape)
 *   • QB-2 · B3 (how the same line reads on the builder page)
 *
 * Wire tokens hit by this component (from W4 `<style>` block):
 *
 *   .lcard   { border:1px solid var(--line); border-radius:var(--r);
 *              background:#fff; margin-bottom:9px; }
 *   .lctop   { display:flex; justify-content:space-between; gap:10px;
 *              padding:10px 12px 8px; }
 *   .lctitle { font-size:12.5px; line-height:1.45; }
 *   .lcmeta  { color:var(--muted); font-size:11.5px; margin-top:2px; }
 *   .lcprice { font-weight:700; font-size:13px; white-space:nowrap;
 *              text-align:right; }
 *   .lcbody  { padding:0 12px 10px; display:flex; flex-direction:column;
 *              gap:6px; }
 *   .lcnote  { font-size:11.5px; color:var(--muted); line-height:1.5; }
 *   .lcnote .more { text-decoration:underline; color:var(--ink); }
 *   .tag     { display:inline-flex; align-items:center; gap:3px;
 *              border:1px solid var(--line2); border-radius:999px;
 *              padding:1px 7px; font-size:10.5px; }
 *   .tag.roll{ background:var(--okbg); border-color:#bfe0cd;
 *              color:var(--ok); }  ← icon:check + no $ value
 *   .tag.item{ background:#fff; color:var(--muted); }  ← outline + $ value
 *   .tag.cost{ background:#fff; border-style:dashed; }  ← labor / added cost
 *
 * Tag order per W4 DOM:
 *   1. labor  (dashed)
 *   2. cost   (dashed)
 *   3. rolled (green + check)
 *   4. itemised (outline + value)
 *
 * Money contract (do NOT regress ACC-5):
 *   The margin engine folds ALL accessories (rolled + itemised) into
 *   `computed.total` via projectAccessoriesToMaterial. The card must:
 *     - display the LINE PRICE as `computed.total − Σ(itemised extended)`
 *       (matches QuoteReviewSend's `displayLineAmount`);
 *     - render itemised accessories as OWN tags carrying their `$` value;
 *     - render rolled-up accessories as VALUE-LESS green check tags.
 *   The AreaCard subtotal continues to sum `computed.total` and therefore
 *   equals `Σ(displayLinePrice) + Σ(itemised extended)` — invariant.
 *
 * Custom-item quantity (PR-06 FIX / operator ruling 2026-07-22):
 *   Custom items may be created with `quantity: null`. When null AND no
 *   `sqft`, the meta line surfaces a "Set quantity" affordance opening an
 *   inline stepper.
 */

import React, { useState, useCallback } from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ReceiptPercentIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import DropdownMenu from '../../ui/DropdownMenu';

// Note truncate threshold — matches PR-19 tuning; ~90 chars keeps the card
// height stable across viewport widths.
const NOTE_TRUNCATE_CHARS = 90;

function fmtCents(cents) {
  return `$${((cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// PR-24 (2026-07-23): dollar formatter for values already in DOLLARS (not cents).
// Used for `additionalLabor` and `additionalCost` which `buildLineItem` stores
// as raw dollar numbers (see seedCatalog.js:231-232 + its test at line 105-106).
// Whole-dollar amounts render without decimals ("$180"); fractional render with
// two decimals ("$180.50"). Matches wire W4/S4 which shows "+$180 labor".
function fmtDollars(dollars) {
  const n = Number(dollars) || 0;
  const hasFraction = n !== Math.trunc(n);
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * IDI-176 B3 CHOKE POINT (2026-09-05, post-#364).
 *
 * Neutralize any Edit-labelled menu entry when the row is a custom line.
 * Runs AFTER the caller's `menuItems || fallback` resolves, so every
 * source — the fallback, ChangeOrderBuilderPage.getMenuItems, or any
 * future override — is covered without each caller having to remember
 * to gate.
 *
 * Rules:
 *   · Only touches menu items whose `label` matches /^Edit$/i.
 *   · Only touches them when `row.isCustom` is truthy.
 *   · Preserves every other property on the menu item (variant, hidden,
 *     custom title, etc.) — replaces only onClick, disabled, and title.
 *   · Non-custom rows pass through untouched — identity of the array is
 *     preserved by only running .map when there's a custom line, so no
 *     unnecessary re-renders.
 *
 * Falsifier: render CommittedLineCard with `menuItems=[{label:'Edit',
 * onClick:fn, ...}]` AND `item.isCustom=true`. Without this helper the
 * Edit entry passes through as enabled and the user hits the broken
 * wizard on the CO builder. Test in CommittedLineCard.test.js under
 * "IDI-176 B3 · choke-point".
 */
export function gateEditForCustomLine(menuItems, row) {
  if (!row || !row.isCustom) return menuItems;
  if (!Array.isArray(menuItems)) return menuItems;
  return menuItems.map((entry) => {
    if (!entry || typeof entry.label !== 'string') return entry;
    if (!/^Edit$/i.test(entry.label)) return entry;
    return {
      ...entry,
      onClick: () => {},
      disabled: true,
      title:
        'Editing custom line items is temporarily unavailable — coming in a follow-up ticket.',
    };
  });
}

export default function CommittedLineCard({
  item,
  computed = {},
  onEdit,
  onRemove,
  onAddAlternate,
  onUpdateQuantity,
  // DEF-160/CO-03: optional override for kebab menu items.
  // When provided, replaces the default Edit/Delete menu.
  menuItems,
}) {
  // ─── Local edit state ─────────────────────────────────────────────────────

  const [noteExpanded, setNoteExpanded] = useState(false);
  const [editingQty, setEditingQty] = useState(false);
  const [qtyInput, setQtyInput] = useState('');

  const { total: engineTotal = 0, margin: engineMargin = 0, marginSource: engineMarginSource } = computed;

  // R20-4 (2026-08-07): margin indicator with tier label.
  // Shows which tier of the precedence chain won for THIS line.
  // Operator ruling: "low-lighted, near the price, every line."
  //
  //   category_default  → "40% category"
  //   line_override     → "40% line override"
  //   customer_override → "40% customer margin"
  //   customer_type_default → "40% customer type"
  //   price_override    → "40% price override" (back-calculated from price)
  //   quote_default     → "40% default margin"
  // §1b (2026-08-08, Lindsey 7.31 rule restored): only show the indicator
  // when the margin is LINE-SPECIFIC. Inherited tiers (customer, customer_type,
  // quote_default) show nothing — the indicator's job is to flag that this
  // particular line is priced differently from the default. Every line
  // "adopting" the default would dilute exactly that signal.
  //
  //   SHOW  category_default · line_override · price_override
  //   HIDE  customer_override · customer_type_default · quote_default
  //
  // IDI-107 Part 3 (2026-08-25): `price_override` is a back-calculation
  // from stored price/cost (marginFromPrice). When the line's own cost
  // resolves to 0 (uncosted catalog row — measured 38% of the pricebook),
  // that back-calculation always yields exactly 100%, which is an
  // arithmetic artifact, not a margin claim (see readEngineLine.js's
  // `hasExplainedMargin`, which is false in this exact state). Showing
  // "0% price override"/"100% price override" on every uncosted line dilutes
  // the badge's entire purpose — flagging the EXCEPTIONAL line — and tells
  // the operator a false thing about their margin. Gate price_override on a
  // real, nonzero cost; render nothing (not a misleading number) otherwise.
  //
  // IDI-309 / IDI-312 (10-02, "no margin a dealer didn't set"): the ONLY chip
  // left is a dealer-set CATEGORY margin, reading "50% margin". The seed
  // category defaults are gone, so `category_default` now means the dealer
  // set it. "NN% line override" and "NN% price override" are removed: an
  // estimate-card re-price writes a price on every eligible line, and a chip
  // on each of them would flag nothing exceptional.
  const marginTierLabel = (() => {
    if (engineMarginSource !== 'category_default' || engineMargin == null) return null;
    if (!Number.isFinite(Number(engineMargin))) return null;
    return `${Math.round(engineMargin * 100)}% margin`;
  })();

  // ─── Money split (line price hides itemised; itemised = own tag) ─────────
  const accessories = Array.isArray(item.accessories) ? item.accessories : [];
  const itemisedExtended = accessories
    .filter((a) => a && a.rollUp === false)
    .reduce(
      (sum, a) => sum + (Number(a.qty) || 0) * (Number(a.unit_cost) || 0),
      0
    );
  const displayLinePrice = Math.max(0, engineTotal - itemisedExtended);

  // ─── Edit handlers ────────────────────────────────────────────────────────
  //
  // IDI-107 Part 2 (2026-08-25 ruling): the per-line PRICE override write
  // affordance (startEditPrice/commitPrice) is removed — "user should not
  // be able to override cost per-line item". It was also a live
  // contradiction with the margin engine: useQuoteMargins.updateLinePrice
  // clears lineTotal on the grounds that margin takes precedence, so a
  // price edit and a margin edit each expected to win depending on order.
  //
  // The READ path stays: `isRealPriceOverride` / the `price_override`
  // margin tier in marginEngine.js are untouched — migrated and
  // already-sent quotes carry stored lineTotals that still have to be
  // back-calculated for display (see marginTierLabel above, now gated by
  // Part 3's cost-unknown fix). Only the write affordance is gone.

  const commitQty = useCallback(() => {
    const n = parseFloat(qtyInput);
    if (Number.isFinite(n) && n >= 0) onUpdateQuantity?.(n);
    setEditingQty(false);
  }, [qtyInput, onUpdateQuantity]);

  // ─── Title + meta line ────────────────────────────────────────────────────
  // PT-32 (2026-07-27) 32.1: sub-area primary, product secondary.
  // Q_10 wireframe emphasises the sub-area as the first line; product
  // ("Open-cell R-38") drops to a secondary line beneath. If a legacy line has
  // no subAreaLabel (customs, older data), we fall through to the product
  // label as the title so the card is never blank.
  const productSecondary = item.productLabel || item.categoryLabel || null;
  const title = item.subAreaLabel || productSecondary || 'Untitled line';
  // If the title came from subarea, product is the secondary line. If subarea
  // was missing and we fell through to the product for the title, don't
  // repeat it below — leave secondary null.
  const secondary = item.subAreaLabel ? productSecondary : null;
  // aria-label helper: when we have both a subarea title and a product
  // secondary, screen readers should hear both to disambiguate. Falls back
  // to the title alone when only one is present.
  const ariaTitle = secondary ? `${secondary} in ${title}` : title;

  // IDI-507 (10): a `metaPieces` list built "R-${rValue}" here (so "R-R-30"
  // for a pricebook rValue) but was never rendered; removed. The R-value a
  // user sees is formatted by quote-builder/materialList displayRValue.

  const needsQty =
    item.isCustom &&
    !item.sqft &&
    (item.quantity == null || item.quantity === '');

  // ─── Tag order (per W4 DOM): labor → cost → rolled → itemised ───────────
  const tags = [];
  if (Number(item.additionalLabor) > 0) {
    tags.push({
      key: 'labor',
      kind: 'cost',
      text: `+${fmtDollars(item.additionalLabor)} labor`,
    });
  }
  if (Number(item.additionalCost) > 0) {
    tags.push({
      key: 'cost',
      kind: 'cost',
      text: `+${fmtDollars(item.additionalCost)} cost`,
    });
  }
  accessories
    .filter((a) => a && a.rollUp !== false)
    .forEach((a, idx) => {
      tags.push({
        key: `roll-${a.id || a.accessory_id || a.name || idx}`,
        kind: 'roll',
        icon: 'check',
        text: `${a.name} ×${Number(a.qty) || 0}`,
      });
    });
  accessories
    .filter((a) => a && a.rollUp === false)
    .forEach((a, idx) => {
      const amt = (Number(a.qty) || 0) * (Number(a.unit_cost) || 0);
      tags.push({
        key: `item-${a.id || a.accessory_id || a.name || idx}`,
        kind: 'item',
        text: `${a.name} ×${Number(a.qty) || 0} · ${fmtCents(amt)} itemised`,
      });
    });

  // ─── Note truncate ────────────────────────────────────────────────────────
  const noteText = (item.notes || '').trim();
  const noteIsLong = noteText.length > NOTE_TRUNCATE_CHARS;
  const notePreview =
    !noteExpanded && noteIsLong
      ? `${noteText.slice(0, NOTE_TRUNCATE_CHARS).trimEnd()}…`
      : noteText;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="rounded-md border bg-white"
      style={{
        borderColor: 'var(--color-border)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--color-surface)',
        marginBottom: 'var(--lc-gap)',
      }}
      data-portica-role="committed-line-card"
    >
      {/* PT-53A W3.2 (DEF-054): inline header row.
          Operator-specified order: sub-area | category | product | sqft | Price
          One row per line item. Accessories, notes, kebab stay below.
          Inline editing (price + qty) preserved in-place. */}
      <div
        className="flex items-center gap-2"
        style={{ padding: 'var(--lc-pad)', minHeight: '40px' }}
      >
        {/* 1. Sub-area */}
        <span
          className="truncate text-sm font-medium"
          style={{ color: 'var(--color-text-primary)', flex: '0 0 auto', maxWidth: '20%' }}
          title={item.subAreaLabel || ''}
        >
          {item.subAreaLabel || '—'}
        </span>

        {/* DEC-43: category removed (product label already carries it).
            Product renders full name, un-truncated.
            §1c (2026-08-08): labelled "Prod:" like other card fields. */}
        {/* 2. Product (full name) */}
        <span
          className="text-sm flex-1 min-w-0"
          style={{ color: 'var(--color-text-default)' }}
          title={item.productLabel || item.description || ''}
        >
          <span style={{ color: 'var(--color-text-muted)' }}>Prod: </span>
          {item.productLabel || item.description || '—'}
        </span>

        {/* 4. Sqft / Qty (inline-editable) */}
        {/* §1c (2026-08-08): show the unit label ("Sqft:", "Lin ft:", etc.)
            in the muted token, followed by the editable value. */}
        <span
          className="text-sm tabular-nums text-right flex-shrink-0 flex items-center gap-1 justify-end"
          style={{ color: 'var(--color-text-muted)', minWidth: '80px' }}
        >
          {(() => {
            const rawUnit = String(item.unit || 'sq ft').toLowerCase();
            // Title-case common unit labels for the prefix.
            const label = rawUnit === 'sq ft' || rawUnit === 'sqft' ? 'Sqft'
              : rawUnit === 'lin ft' || rawUnit === 'linear ft' ? 'Lin ft'
              : rawUnit === 'ea' ? 'Qty'
              : rawUnit.charAt(0).toUpperCase() + rawUnit.slice(1);
            return <span>{label}:</span>;
          })()}
          {editingQty ? (
            <input
              type="number"
              min="0"
              value={qtyInput}
              onChange={(e) => setQtyInput(e.target.value)}
              onBlur={commitQty}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitQty();
                if (e.key === 'Escape') setEditingQty(false);
              }}
              autoFocus
              className="w-14 border rounded px-1 py-0.5 text-right"
              style={{ borderColor: 'var(--color-border)', fontSize: '0.8125rem' }}
              aria-label={`Set quantity for ${ariaTitle}`}
            />
          ) : (
            <button
              type="button"
              onClick={() => { setQtyInput(String(item.sqft || item.quantity || '')); setEditingQty(true); }}
              className="hover:underline"
              title="Click to edit quantity"
            >
              {item.sqft || item.quantity || (needsQty ? <span className="underline" style={{ color: 'var(--color-action-primary)' }}>Set quantity</span> : '—')}
            </button>
          )}
        </span>

        {/* IDI-95: the item's category has NO Margins & Labor bucket to
            price from (marginKey===null — e.g. Removal, or any tenant-only
            category), so it silently fell through to the quote/customer
            default margin instead of the tenant's category-specific rate.
            Previously invisible — the operator would never know this line
            wasn't priced the way they configured Margins & Labor to price
            it. Rendered as a warning, distinct from the (neutral) margin-
            tier indicator below. */}
        {item.hasCategoryMarginBucket === false && (
          <span
            className="inline-flex items-center gap-1 text-xs font-normal whitespace-nowrap flex-shrink-0"
            style={{ color: 'var(--color-warning)' }}
            title="This item's category has no margin bucket in Settings → Margins & Labor — priced with the quote/customer default instead."
            data-testid="no-margin-bucket-warning"
          >
            <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            No margin bucket
          </span>
        )}

        {/* §1a (2026-08-08): R20-4 margin indicator LEFT of price, with a
            receipt-percent icon. Only renders for line-specific tiers per §1b. */}
        {marginTierLabel && (
          <span
            className="inline-flex items-center gap-1 text-xs font-normal tabular-nums whitespace-nowrap flex-shrink-0"
            style={{ color: 'var(--color-text-muted)' }}
            title="This line is priced with a specific margin (Lindsey 7.31 rule)"
          >
            <ReceiptPercentIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {marginTierLabel}
          </span>
        )}

        {/* 5. Price — IDI-107 Part 2: read-only. The per-line price override
            write affordance was removed (operator ruling); this now renders
            the engine-computed price with no click-to-edit. */}
        <span
          className="text-sm font-bold tabular-nums text-right flex-shrink-0"
          style={{ minWidth: '80px', color: 'var(--color-text-primary)' }}
          aria-label={`Price for ${ariaTitle}`}
        >
          {fmtCents(displayLinePrice)}
        </span>

        {/* Kebab menu */}
        <div className="flex-shrink-0">
          {/* IDI-176 B3 · CHOKE POINT (2026-09-05, post-#364).
              Custom lines cannot round-trip through the wizard (categoryId
              'custom' is not in the droplist, productId null, sqft null →
              isDraftComplete false → SAVE_ITEM no-ops → dead form). Retired
              EditItemModal was the correct editor; the follow-up ticket
              will land the real custom-line editor.

              #364 gated Edit ONLY on the fallback (default) menu:
                  items={menuItems || [ …gated Edit… ]}
              But `menuItems` is a documented override prop (:96-98) used by
              ChangeOrderBuilderPage.js's getMenuItems at :564-567 — its
              Edit entry ignores isCustom and re-opens the broken wizard.
              #363 hid two consumers of EditItemModal; #364 hid two
              reducer cases with one vocabulary; #365 hides two menu
              sources into one card. The pattern is stable: when fixing a
              shared component, enumerate its overrides and check that
              each cannot bypass what you just did.

              CHOKE POINT: compute the resolved menu FIRST (whether it
              came from the override or the fallback), then map over it
              to neutralize any item whose `label` matches /^Edit$/i on
              custom lines. Every override — present and future — is now
              covered without duplicating the gate at each call site. */}
          <DropdownMenu
            ariaLabel={`Actions for ${ariaTitle}`}
            items={gateEditForCustomLine(
              menuItems || [
                { label: 'Edit', onClick: onEdit },
                onAddAlternate
                  ? { label: 'Add alternative', onClick: onAddAlternate }
                  : { hidden: true },
                { label: 'Delete', variant: 'danger', onClick: onRemove },
              ],
              item
            )}
          />
        </div>
      </div>

      {/* ─── .lcbody: note · tags · internal editor ────────────────────── */}
      {(noteText || tags.length > 0) && (
        <div
          className="flex flex-col"
          style={{ padding: 'var(--lc-body-pad)', gap: 'var(--lc-body-gap)' }}
        >
          {/* Note (inline, truncated, "more" toggle) */}
          {noteText && (
            <div
              style={{
                fontSize: 'var(--lc-meta-size)',
                color: 'var(--color-text-muted)',
                lineHeight: 1.5,
              }}
            >
              {/* PR-24 (2026-07-23): quote the note per wire S4/W4 style
                  (`<div class="note">"Standard 2-inch lift..."</div>`).
                  Uses typographic curly quotes to feel like a quoted remark. */}
              <span>“{notePreview}”</span>
              {noteIsLong && (
                <button
                  type="button"
                  onClick={() => setNoteExpanded((v) => !v)}
                  className="ml-1"
                  style={{
                    color: 'var(--color-text-primary)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  aria-label={
                    noteExpanded ? 'Show less of note' : 'Show more of note'
                  }
                >
                  {noteExpanded ? 'less' : 'more'}
                </button>
              )}
            </div>
          )}

          {/* Tag row: labor → cost → rolled → itemised (per W4 DOM) */}
          {tags.length > 0 && (
            <div
              className="flex flex-wrap"
              style={{ gap: 'var(--lc-tag-row-gap)' }}
              data-portica-role="committed-line-tags"
            >
              {tags.map((t) => (
                <span
                  key={t.key}
                  className="inline-flex items-center"
                  style={{
                    gap: 'var(--lc-tag-gap)',
                    padding: 'var(--lc-tag-pad)',
                    fontSize: 'var(--lc-tag-size)',
                    borderRadius: '999px',
                    // Base border applies to `item`; `roll` overrides bg/color/border;
                    // `cost` overrides border-style to dashed.
                    borderWidth: '1px',
                    borderStyle: t.kind === 'cost' ? 'dashed' : 'solid',
                    // Wire W4 uses raw hex #bfe0cd for the green roll-up tag
                    // border; the closest semantic token in the ramp is
                    // --green-100 (#C8E7D4) which conforms to R4 (no raw hex).
                    borderColor:
                      t.kind === 'roll'
                        ? 'var(--green-100)'
                        : 'var(--color-border)',
                    background:
                      t.kind === 'roll'
                        ? 'var(--color-success-bg)'
                        : 'var(--color-surface)',
                    color:
                      t.kind === 'roll'
                        ? 'var(--color-success)'
                        : 'var(--color-text-muted)',
                  }}
                  data-tag-kind={t.kind}
                >
                  {t.icon === 'check' && (
                    <CheckIcon className="h-3 w-3" aria-hidden="true" />
                  )}
                  {t.text}
                </span>
              ))}
            </div>
          )}


        </div>
      )}

      {/* ─── Alternates (indented sub-rows, excluded from totals) ────────── */}
      {Array.isArray(item.alternates) && item.alternates.length > 0 && (
        <div
          style={{
            padding: 'var(--lc-alt-pad)',
            borderTop: '1px dashed var(--color-border)',
          }}
        >
          {item.alternates.map((alt) => (
            <div
              key={alt.id || alt.productLabel}
              className="flex items-center justify-between"
              style={{
                padding: '6px 0',
                fontSize: 'var(--lc-meta-size)',
                color: 'var(--color-text-muted)',
              }}
            >
              <div className="min-w-0 flex items-center gap-2">
                <span
                  className="font-medium"
                  style={{
                    padding: '1px 4px',
                    fontSize: '10px',
                    borderRadius: '3px',
                    backgroundColor: 'var(--red-50)',
                    color: 'var(--red-700)',
                  }}
                >
                  ALT
                </span>
                <span className="truncate">{alt.productLabel}</span>
              </div>
              <span
                className="font-medium tabular-nums whitespace-nowrap"
                style={{ fontSize: 'var(--lc-meta-size)' }}
              >
                {fmtCents(alt.total || 0)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
